/**
 * StateChip - Base interactive state display component
 *
 * A reusable chip component that displays a state with optional popover interaction.
 * This base component provides the visual display and interactive behavior,
 * while entity-specific implementations handle state logic and history.
 */

import { useState, type ReactNode } from "react";
import { Badge, Tooltip, Popover } from "@humansignal/ui";
import { useTranslation } from "@humansignal/core";

export interface StateChipProps {
  /**
   * Visual label to display in the chip
   */
  label: string;

  /**
   * Description for the tooltip when not interactive
   */
  description?: string;

  /**
   * Badge variant for styling (e.g. "sand", "grape", "canteloupe", "kale", "plum").
   * Use getStateVariant(state) from @humansignal/app-common to map state to variant.
   */
  variant: string;

  /**
   * Whether the chip should be interactive (clickable with popover)
   */
  interactive?: boolean;

  /**
   * Content to display in the popover when interactive
   */
  popoverContent?: ReactNode;

  /**
   * Controlled open state for the popover
   */
  open?: boolean;

  /**
   * Callback when popover open state changes
   */
  onOpenChange?: (open: boolean) => void;
}

export function StateChip({
  label,
  description,
  variant,
  interactive = false,
  popoverContent,
  open: controlledOpen,
  onOpenChange,
}: StateChipProps) {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  // Non-interactive chip - just show the badge with tooltip
  if (!interactive || !popoverContent) {
    return (
      <Tooltip title={description || label}>
        <span>
          <Badge variant={variant} shape="rounded">
            {label}
          </Badge>
        </span>
      </Tooltip>
    );
  }

  // Interactive chip with popover
  const trigger = (
    <button
      className="cursor-pointer outline-none hover:opacity-80 transition-opacity"
      onClick={(e) => {
        e.stopPropagation();
        setOpen(!isOpen);
      }}
      type="button"
      title={t("libs.ui.lib.state-chip.state-chip.click_to_view_history")}
    >
      <Badge variant={variant} shape="rounded">
        {label}
      </Badge>
    </button>
  );

  return (
    <Popover trigger={trigger} open={isOpen} onOpenChange={setOpen} align="start" sideOffset={8}>
      {popoverContent}
    </Popover>
  );
}
